import type { ReactNode } from 'react';
import { toast as heroToast } from '@heroui/react';

/**
 * Sonner-compatible toast adapter backed by HeroUI v3's `toast`.
 *
 * The app historically called `sonner`'s `toast.*` API in ~25 places. We
 * migrated notifications onto HeroUI's native Toast (rendered by
 * `<Toast.Provider />` in App.tsx) without rewriting every callsite by
 * exposing the same surface here and translating the option shape:
 *
 *   - sonner `toast.error`     → HeroUI `toast.danger`
 *   - sonner `toast.message`   → HeroUI default `toast`
 *   - sonner `{ duration }`    → HeroUI `{ timeout }`
 *   - sonner `{ onDismiss }`   → HeroUI `{ onClose }`
 *   - sonner `toast.dismiss()` → HeroUI `toast.close(id)` / `toast.clear()`
 *
 * Variants (success / danger / warning / accent) inherit the design tokens
 * from HeroUI's own variant styles, so we no longer hand-wire CSS vars.
 */

type ToastMessage = ReactNode;

/** sonner's action button: `{ label, onClick }`. */
interface SonnerToastAction {
  label: ReactNode;
  onClick: () => void;
}

/** The subset of sonner's option bag the codebase actually uses. */
interface SonnerToastOptions {
  description?: ReactNode;
  /** sonner uses `duration` (ms); HeroUI uses `timeout`. */
  duration?: number;
  /** sonner fires onDismiss/onAutoClose; HeroUI has a single onClose. */
  onDismiss?: () => void;
  onAutoClose?: () => void;
  /** sonner action button → HeroUI actionProps. */
  action?: SonnerToastAction;
}

interface HeroToastOptions {
  description?: ReactNode;
  timeout?: number;
  onClose?: () => void;
  actionProps?: { children: ReactNode; onPress: () => void };
}

function mapOptions(opts?: SonnerToastOptions): HeroToastOptions | undefined {
  if (!opts) return undefined;
  const out: HeroToastOptions = {};
  if (opts.description != null) out.description = opts.description;
  if (typeof opts.duration === 'number') out.timeout = opts.duration;
  if (opts.onDismiss || opts.onAutoClose) {
    out.onClose = () => {
      opts.onDismiss?.();
      opts.onAutoClose?.();
    };
  }
  if (opts.action) {
    out.actionProps = { children: opts.action.label, onPress: opts.action.onClick };
  }
  return out;
}

interface PromiseMessages<T> {
  loading: ReactNode;
  success: ReactNode | ((data: T) => ReactNode);
  error: ReactNode | ((err: unknown) => ReactNode);
}

interface ToastApi {
  (message: ToastMessage, opts?: SonnerToastOptions): string;
  success(message: ToastMessage, opts?: SonnerToastOptions): string;
  error(message: ToastMessage, opts?: SonnerToastOptions): string;
  warning(message: ToastMessage, opts?: SonnerToastOptions): string;
  info(message: ToastMessage, opts?: SonnerToastOptions): string;
  /** sonner's neutral toast — maps to HeroUI's default variant. */
  message(message: ToastMessage, opts?: SonnerToastOptions): string;
  dismiss(id?: string): void;
  promise<T>(promise: Promise<T>, messages: PromiseMessages<T>): Promise<T>;
}

const base = (message: ToastMessage, opts?: SonnerToastOptions): string =>
  heroToast(message, mapOptions(opts));

const toast = base as ToastApi;

toast.success = (message, opts) => heroToast.success(message, mapOptions(opts));
toast.error = (message, opts) => heroToast.danger(message, mapOptions(opts));
toast.warning = (message, opts) => heroToast.warning(message, mapOptions(opts));
toast.info = (message, opts) => heroToast.info(message, mapOptions(opts));
toast.message = (message, opts) => heroToast(message, mapOptions(opts));
toast.dismiss = (id) => {
  if (id) heroToast.close(id);
  else heroToast.clear();
};
toast.promise = (promise, messages) => {
  void heroToast.promise(promise, messages);
  return promise;
};

export { toast };
